import { request } from '../request';

/**
 * 获取 OSS 预签名上传地址
 * POST /assets/uploads/${policy}/presign
 *
 * 流程：选择固定访问策略路由与场景 → 获取预签名地址 → 直传 OSS → 通用确认接口。
 *
 * 固定访问策略路由 `policy` 可选项：
 * - `public-when-published`: 所有者或发布后公开（用于大部分公开或待发布内容，如 DEMAND, SOLUTION, PROFILE 等）
 * - `parent-visible`: 所有者或父对象可见时访问（用于附属内容的场景，如 COMMUNITY 社区帖子配图/视频、COMMENT 评论图、SUPPORT_TICKET 工单附件）
 * - `uploader-only`: 仅上传者可访问（用于私有资质/认证/授权文件，如 SUBJECT 主体认证、APPLICATION 发行材料）
 * - `resource-participant`: 业务资源参与方可访问（用于合作与结算文件，如 LEAD 合作线索、PROJECT 项目、CONTRACT 合同、REVENUE 财务报表）
 * - `entitled`: 所有者或权益用户可访问（用于付费/授权内容，如 CATALOG 商城资源、LEARNING_RESOURCE 学习资源）
 * - `backend-only`: 仅后台可访问（用于内部运营等仅后台具备路由权限的场景，如 INTERNAL）
 *
 * @param policy 访问策略路由 (前台使用 Api.Upload.AccessPolicyRoute)
 * @param data 请求体参数 (file_md5, file_name, file_type, mime_type, scene, ...)
 */
export function fetchPresignUpload(
  policy: Api.Upload.AccessPolicyRoute | string = 'public-when-published',
  data: Api.Upload.CreatePresignedUploadRequest
) {
  return request<Api.Upload.PresignedUploadResponse>({
    url: `/assets/uploads/${policy}/presign`,
    method: 'post',
    data
  });
}

/**
 * 确认 OSS 上传完成
 * POST /assets/uploads/{asset_id}/confirm
 *
 * 服务端校验 OSS 对象后将上传记录更新为 AVAILABLE。
 *
 * @param asset_id 上传资产 ID
 */
export function fetchConfirmUpload(asset_id: string | number) {
  return request<Api.Upload.ConfirmUploadResponse>({
    url: `/assets/uploads/${asset_id}/confirm`,
    method: 'post'
  });
}

/**
 * 续签 OSS 资产访问地址
 * POST /assets/{asset_id}/access-url
 *
 * 按资产已保存的访问策略鉴权后，返回短期有效的 OSS 访问地址。当前前台仅允许续签本人上传的非后台资产。
 *
 * @param asset_id 上传资产 ID
 */
export function fetchRenewAccessUrl(asset_id: string | number) {
  return request<Api.Upload.RenewAccessURLResponse>({
    url: `/assets/${asset_id}/access-url`,
    method: 'post'
  });
}

/**
 * 按业务资源上下文批量续签 OSS 访问地址
 * POST /assets/access-urls/renew
 *
 * 前台必须提交资源类型、资源标识和资源实际关联的资产标识。每项由对应业务模块鉴权；单次最多 100 个资产。
 * 求职申请使用 resource_type=JOB_APPLICATION、resource_id=申请 ID，可由申请人或目标岗位所属企业续签简历文件和作品附件。
 *
 * @param data 请求体数据 (resources)
 */
export function fetchRenewResourceAccessUrls(data: Api.Upload.RenewResourceAccessURLsRequest) {
  return request<Api.Upload.RenewResourceAccessURLsResponse>({
    url: '/assets/access-urls/renew',
    method: 'post',
    data
  });
}


